// ---------------------------------------------------------------------------
// The assessment card in the expanded lead (NDI-239 v2)
//
// Mounted by campaign-detail.ts's renderLeadDetail with one line,
// LeadAssessment({...}), which places <sdr-lead-assessment>: a LitElement in
// the light DOM (display: contents, so it adds no box to the lead's column).
// Renders nothing unless the account has the `assessmentAgent` flag and at
// least one approved setup — or the lead already has an assessment: a setup
// whose approval went away (it was changed) hides only the start, never the
// lead's result, and says where to approve it again (R33). Then: the website
// to research (prefilled from the lead's email domain unless it is freemail —
// leads carry no website), "Create assessment", and the lead's newest
// assessment — polled every 3 s while it runs (a Poller, which stops when the
// card leaves the page). A checked one shows 2–3 key numbers and the hook with
// an icon-only copy button right after it; then one row of actions in one size:
// "Open PDF", "Copy notes for the message" (the hand-over's „Gelesen: …
// Angenommen: …“, copied, not printed a second time) and, at its end, "Run again";
// folded under "How it was calculated", what was found on the website (with
// its quote, visible, and its source) and what was assumed (with its basis).
// A blocked one shows the server's reasons and "Run again"; on a checked one,
// "Run again" is one quiet button that opens the website field (R36). A
// refused start is read by its code: a setup problem (SETUP_NOT_APPROVED,
// SETUP_REQUIRED) shows the server's reason as a warning with "Open setup",
// and the button waits until another setup is chosen; only a 409 that names
// an assessmentId (one is already
// running) shows that one; any other refusal (no website, no company …) stays
// on the card in the server's German words (R34). Nothing is sent to the lead
// from here.
//
// German text from the server (reasons, labels, hook, quotes) is marked
// lang="de". No Adornis, no baseQL, no router, no toast (it needs
// @adornis/chemistry): short messages show on the card. Colours only from the
// theme's variables (look.ts).
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type PropertyValues, type TemplateResult } from 'lit';

import { toneDot } from '../campaign-status.js';
import { icon } from '../design/icons.js';
import { API, ApiError, type AssessmentView, callApi, messageOf, type SetupView, urlOf } from './api.js';
import { Notice, Poller } from './controllers.js';
import { hasFeature } from './features.js';
import {
  approvedSetups,
  assessmentLook,
  factLists,
  handoverKeyNumbers,
  isRunning,
  latestForLead,
  startRefusal,
  suggestWebsite,
} from './lead-model.js';
import { buttonClass, CARD_CLASS, FIELD_CLASS, FOLD_CLASS, foldSummary, LINK_CLASS, spinner, WARNING_CLASS } from './look.js';

/** Why the last start was refused, on the card until the next one; `setup` adds the link to the setup page. */
interface Problem {
  reason: string;
  german: boolean;
  setup: boolean;
}

export class SdrLeadAssessment extends LitElement {
  static override properties = {
    campaignId: { attribute: false },
    leadId: { attribute: false },
    email: { attribute: false },
    company: { attribute: false },
    pollMs: { attribute: false },
    setups: { state: true },
    latest: { state: true },
    website: { state: true },
    setupId: { state: true },
    busy: { state: true },
    problem: { state: true },
    restart: { state: true },
    copied: { state: true },
  };

  declare campaignId: string;
  declare leadId: string;
  declare email: string | undefined;
  /** The lead's company — the server reads it from the lead itself; kept for the card's future wording. */
  declare company: string | undefined;
  declare pollMs: number;

  /** The approved setups; null until loaded (and without the flag). */
  protected declare setups: SetupView[] | null;
  protected declare latest: AssessmentView | null;
  protected declare website: string;
  protected declare setupId: string;
  protected declare busy: boolean;
  protected declare problem: Problem | null;
  /** Under a checked result the start form opens on demand. */
  protected declare restart: boolean;
  /** Which copy button just copied ('hook', 'notes'); it says so ("Copied", a check) for a moment. */
  protected declare copied: string;

  private readonly poller = new Poller(this);
  private readonly notice = new Notice(this);
  /** Bumped when the card is left or shows another lead: answers for an older one are dropped. */
  private generation = 0;
  private polling = false;
  private starting = false;
  private copiedTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    super();
    this.campaignId = '';
    this.leadId = '';
    this.pollMs = 3000;
    this.reset();
  }

  /** The state of a card that has not loaded yet. */
  private reset() {
    this.setups = null;
    this.latest = null;
    this.website = suggestWebsite(this.email);
    this.setupId = '';
    this.busy = false;
    this.problem = null;
    this.restart = false;
    this.copied = '';
  }

  override createRenderRoot() {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    if (!this.style.display) this.style.display = 'contents';
    // Moved back into the page: what was in flight when it left is gone, so load again.
    if (this.hasUpdated) {
      this.busy = false;
      void this.load();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.generation++;
    this.polling = false;
    this.starting = false;
    clearTimeout(this.copiedTimer);
    this.copiedTimer = undefined;
  }

  protected override willUpdate(changed: PropertyValues<this>) {
    // Another lead in the same element (the panel shows the next one): everything starts over.
    if (this.hasUpdated && !(changed.has('leadId') || changed.has('campaignId'))) return;
    if (this.hasUpdated) {
      this.generation++;
      this.poller.stop();
      this.notice.clear();
    }
    this.reset();
    void this.load();
  }

  protected override updated() {
    // Polling while a pipeline works on it.
    const id = this.latest?.id ?? '';
    const key = isRunning(this.latest?.status) && id ? `${this.generation}:${id}:${this.pollMs}` : '';
    this.poller.run(key, this.pollMs, () => void this.poll(id));
  }

  private async load() {
    const gen = this.generation;
    const campaignId = this.campaignId;
    const leadId = this.leadId;
    if (!leadId) return;
    if (!(await hasFeature('assessmentAgent')) || gen !== this.generation) return;
    let approved: SetupView[];
    try {
      const data = await callApi<{ setups?: SetupView[] }>(API.listSetups);
      approved = approvedSetups(Array.isArray(data.setups) ? data.setups : []);
    } catch (err) {
      // The card is an extra in someone else's panel: no message on every expansion.
      console.warn('[assessment] setups could not be loaded', err);
      return;
    }
    if (gen !== this.generation) return;
    if (approved[0]) this.setupId = approved[0]._id;
    // The lead's result is shown even when no setup is approved now (R33).
    try {
      const data = await callApi<{ assessments?: AssessmentView[] }>(API.listAssessments, {
        query: { campaignId },
      });
      if (gen === this.generation) {
        this.latest = latestForLead(Array.isArray(data.assessments) ? data.assessments : [], leadId) ?? null;
      }
    } catch (err) {
      console.warn('[assessment] assessments could not be loaded', err);
    }
    if (gen === this.generation) this.setups = approved;
  }

  private async poll(id: string) {
    if (this.polling) return;
    const gen = this.generation;
    this.polling = true;
    try {
      const data = await callApi<{ assessment: AssessmentView }>(API.getAssessment, { id });
      if (gen === this.generation && data.assessment?.id === id) this.latest = data.assessment;
    } catch (err) {
      console.warn('[assessment] polling failed', err);
    } finally {
      if (gen === this.generation) this.polling = false;
    }
  }

  private async start() {
    if (this.starting) return;
    const gen = this.generation;
    this.starting = true;
    this.busy = true;
    this.problem = null;
    this.notice.clear();
    const live = () => gen === this.generation;
    try {
      const site = this.website.trim();
      const setupId = this.setupId;
      const data = await callApi<{ assessment: AssessmentView }>(API.startAssessment, {
        body: {
          campaignId: this.campaignId,
          leadId: this.leadId,
          ...(site ? { website: site } : {}),
          ...(setupId ? { setupId } : {}),
        },
      });
      if (live()) {
        this.latest = data.assessment;
        this.restart = false;
      }
    } catch (err) {
      const refusal =
        err instanceof ApiError
          ? startRefusal(err.status, err.body, err.message)
          : { kind: 'error' as const, reason: messageOf(err) };
      if (refusal.kind === 'running') {
        try {
          const data = await callApi<{ assessment: AssessmentView }>(API.getAssessment, { id: refusal.assessmentId });
          if (live()) {
            this.latest = data.assessment;
            this.notice.show('An assessment is already running for this lead.');
          }
        } catch (inner) {
          if (live()) this.problem = { reason: messageOf(inner), german: inner instanceof ApiError, setup: false };
        }
      } else if (live()) {
        this.problem = {
          reason: refusal.reason,
          german: err instanceof ApiError && typeof err.body.error === 'string',
          setup: refusal.kind === 'setup',
        };
      }
    } finally {
      if (live()) {
        this.starting = false;
        this.busy = false;
      }
    }
  }

  private async copy(value: string, which: string) {
    const gen = this.generation;
    try {
      await navigator.clipboard.writeText(value);
      if (gen !== this.generation) return;
      this.copied = which;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copiedTimer = undefined;
        this.copied = '';
      }, 2000);
    } catch {
      if (gen === this.generation) this.notice.show('Could not copy — select the text instead.', 6000);
    }
  }

  /** A copy button that is only its icon, right after the text it copies; a check for a moment once copied. */
  private copyButton(value: string, which: string, name: string): TemplateResult {
    const done = this.copied === which;
    return html`<button
      type="button"
      class=${buttonClass('quiet')}
      aria-label=${done ? 'Copied' : name}
      title=${name}
      data-copy=${which}
      @click=${() => void this.copy(value, which)}
    >
      ${icon({ name: done ? 'check' : 'duplicate', size: 16 })}
    </button>`;
  }

  private setupLink(): TemplateResult {
    return html`<a class="${LINK_CLASS} whitespace-nowrap" href="/assessment">Open setup</a>`;
  }

  /**
   * The website and "Create assessment" (or "Run again"). A refusal stays below it; a setup problem (not approved,
   * which setup) is a warning with "Open setup", and the button waits — the same click would be refused again —
   * until another setup is chosen.
   */
  private startForm(label: string, primary: boolean): TemplateResult {
    const setups = this.setups ?? [];
    if (setups.length === 0) {
      return html`<p class="text-xs text-(--sdr-ink-muted)">
        No approved setup — new assessments start once one is approved. ${this.setupLink()}
      </p>`;
    }
    const running = isRunning(this.latest?.status);
    const problem = this.problem;
    return html`
      <form
        class="flex flex-wrap items-center gap-2"
        @submit=${(e: Event) => {
          e.preventDefault();
          void this.start();
        }}
      >
        <input
          class="${FIELD_CLASS.sm} flex-1"
          name="website"
          aria-label="Website to research"
          placeholder="Website, e.g. school.de"
          .value=${this.website}
          @input=${(e: Event) => {
            this.website = (e.target as HTMLInputElement).value;
          }}
        />
        ${
          setups.length > 1
            ? html`<select
              class="${FIELD_CLASS.sm} w-40"
              aria-label="Setup"
              @change=${(e: Event) => {
                this.setupId = (e.target as HTMLSelectElement).value;
                // Another setup is another request: a setup refusal no longer holds.
                if (this.problem?.setup) this.problem = null;
              }}
            >
              ${setups.map(s => html`<option value=${s._id} ?selected=${s._id === this.setupId}>${s.name || 'Untitled setup'}</option>`)}
            </select>`
            : nothing
        }
        <button
          type="submit"
          class=${buttonClass(primary ? 'primary' : 'quiet')}
          ?disabled=${this.busy || running || !!problem?.setup}
        >
          ${this.busy ? spinner() : nothing} ${label}
        </button>
      </form>
      ${
        problem?.setup
          ? html`<div class="${WARNING_CLASS} flex flex-col items-start gap-2 text-xs" role="alert" data-setup-problem>
              <p lang=${problem.german ? 'de' : nothing}>${problem.reason}</p>
              <a class=${buttonClass('plain', 'xs')} href="/assessment">Open setup</a>
            </div>`
          : problem
            ? html`<p class="text-xs text-(--color-error)" role="alert" lang=${problem.german ? 'de' : nothing}>
                ${problem.reason}
              </p>`
            : nothing
      }
    `;
  }

  /**
   * A checked assessment: the key numbers and the hook first, then two actions — "Open PDF" (the deliverable) and
   * "Copy notes for the message" — and, folded under "How it was calculated", what was found (with its quote, visible
   * on touch too) and what was assumed.
   */
  private checkedBody(a: AssessmentView): TemplateResult {
    const numbers = handoverKeyNumbers(a);
    const hook = a.handover?.hook ?? a.hook ?? '';
    const notes = a.handover?.messageNotes?.trim() ?? '';
    const { found, assumed } = factLists(a);
    const setups = this.setups ?? [];
    const running = isRunning(this.latest?.status);
    const heading = (title: string) => html`<h5 class="text-xs font-medium text-(--sdr-ink-muted)">${title}</h5>`;
    const none = html`<p class="text-xs text-(--sdr-ink-subtle)">Nothing.</p>`;
    return html`
      ${
        numbers.length
          ? html`<div class="flex flex-wrap gap-x-6 gap-y-2">
            ${numbers.map(
              n => html`<div class="flex flex-col">
                <span class="text-lg font-medium">${n.text}</span>
                <span class="text-xs text-(--sdr-ink-muted)" lang="de">${n.label}</span>
              </div>`,
            )}
          </div>`
          : nothing
      }
      ${
        hook
          ? html`<div class="flex items-start gap-1">
            <p class="min-w-0 pt-1.5 text-sm" lang="de" data-hook>${hook}</p>
            ${this.copyButton(hook, 'hook', 'Copy hook')}
          </div>`
          : nothing
      }
      <div class="flex flex-wrap items-center gap-2">
        <a class=${buttonClass('plain')} href=${urlOf(API.assessmentPdf, a.id)} target="_blank" rel="noopener"
          >${icon({ name: 'document', size: 16 })} Open PDF</a
        >
        ${
          notes
            ? html`<button type="button" class=${buttonClass('quiet')} data-copy-notes @click=${() => void this.copy(notes, 'notes')}>
                ${icon({ name: 'duplicate', size: 16 })} ${this.copied === 'notes' ? 'Copied' : 'Copy notes for the message'}
              </button>`
            : nothing
        }
        ${
          // One row of actions, one size: "Run again" at its end opens the start form below the result.
          this.restart || setups.length === 0
            ? nothing
            : html`<button
                type="button"
                class="${buttonClass('quiet')} ml-auto"
                ?disabled=${running}
                @click=${() => {
                  this.restart = true;
                }}
              >
                Run again
              </button>`
        }
      </div>
      <details class=${FOLD_CLASS} data-how>
        ${foldSummary('How it was calculated')}
        <div class="mt-2 grid gap-3 sm:grid-cols-2">
          <div data-facts="found" class="flex flex-col gap-1">
            ${heading('Found')}
            ${
              found.length === 0
                ? none
                : html`<ul class="flex flex-col gap-2 text-sm">
                  ${found.map(
                    f => html`<li class="flex min-w-0 flex-col gap-0.5">
                      <span><span class="font-medium">${f.value}</span> <span lang="de">${f.label}</span></span>
                      ${f.quote ? html`<span class="text-xs text-(--sdr-ink-muted) italic" lang="de" data-quote>„${f.quote}“</span>` : nothing}
                      ${
                        f.url
                          ? html`<a
                            class="block truncate text-xs text-(--sdr-ink-subtle) underline-offset-2 hover:underline"
                            href=${f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            >${f.host}</a
                          >`
                          : html`<span class="block truncate text-xs text-(--sdr-ink-subtle)">${f.host}</span>`
                      }
                    </li>`,
                  )}
                </ul>`
            }
          </div>
          <div data-facts="assumed" class="flex flex-col gap-1">
            ${heading('Assumed')}
            ${
              assumed.length === 0
                ? none
                : html`<ul class="flex flex-col gap-2 text-sm">
                  ${assumed.map(
                    f => html`<li class="flex min-w-0 flex-col gap-0.5">
                      <span><span class="font-medium">${f.value}</span> <span lang="de">${f.label}</span></span>
                      <span class="text-xs text-(--sdr-ink-muted)" lang="de">${f.basis}</span>
                    </li>`,
                  )}
                </ul>`
            }
          </div>
        </div>
      </details>
      ${this.restart || setups.length === 0 ? this.startForm('Run again', false) : nothing}
    `;
  }

  override render() {
    const setups = this.setups;
    const latest = this.latest;
    if (!setups || (setups.length === 0 && !latest)) return nothing;

    const body = !latest
      ? html`${this.startForm('Create assessment', true)}
        ${
          // The hint is for a first look; next to a refusal it is one line too many.
          this.problem
            ? nothing
            : html`<p class="text-xs text-(--sdr-ink-subtle)">Reads the public website, computes and makes a PDF. Nothing is sent.</p>`
        }`
      : latest.status === 'checked'
        ? this.checkedBody(latest)
        : latest.status === 'blocked'
          ? html`<ul class="flex list-disc flex-col gap-1 pl-5 text-sm" lang="de">
                ${(latest.blockedReasons ?? []).map(r => html`<li>${r}</li>`)}
              </ul>
              ${this.startForm('Run again', true)}`
          : html`<p class="flex items-center gap-2 text-xs text-(--sdr-ink-muted)" role="status">
              ${spinner()} Working on it — usually under a minute.
            </p>`;

    return html`
      <div class="${CARD_CLASS} flex flex-col gap-3 p-4">
        <div class="flex items-center justify-between gap-2">
          <h4 class="text-sm font-medium">Assessment</h4>
          ${latest ? toneDot(assessmentLook(latest.status)) : nothing}
        </div>
        ${body}
        ${this.notice.text ? html`<p class="text-xs text-(--sdr-ink-muted)" role="status" data-notice>${this.notice.text}</p>` : nothing}
      </div>
    `;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('sdr-lead-assessment')) {
  customElements.define('sdr-lead-assessment', SdrLeadAssessment);
}

declare global {
  interface HTMLElementTagNameMap {
    'sdr-lead-assessment': SdrLeadAssessment;
  }
}

/** The card for one lead, placed with one line in the expanded lead (campaign-detail.ts). */
export function LeadAssessment(p: {
  campaignId: string;
  leadId: string;
  email?: string;
  company?: string;
  pollMs?: number;
}): TemplateResult {
  return html`<sdr-lead-assessment
    .campaignId=${p.campaignId}
    .leadId=${p.leadId}
    .email=${p.email}
    .company=${p.company}
    .pollMs=${p.pollMs ?? 3000}
  ></sdr-lead-assessment>`;
}
